import { forwardRef, useEffect, useImperativeHandle } from 'react';
import { FormField, FormInput, FormSelect, FormSectionTitle } from '@practium/ui';
import type { TestimonialFormData } from '../../../types/landing.types';
import type { FormPageMode } from '@practium/ui';
import { STATUS_OPTIONS } from '../../../constants/landing.constants';
import { useTestimonialForm } from '../../../hooks/useTestimonialForm';
import styles from './TestimonialForm.module.css';

const STATUS_LABELS: Record<string, string> = { yes: 'Sim', no: 'Não' };

export interface TestimonialFormRef {
	submit: () => Promise<void>;
	isDirty: () => boolean;
}

interface TestimonialFormProps {
	mode: FormPageMode;
	initialData?: TestimonialFormData;
	testimonialId?: string;
	avatarFile?: File | null;
	onSubmit: () => void;
	onLoadingChange: (loading: boolean) => void;
	onFormChange?: (data: TestimonialFormData) => void;
}

export const TestimonialForm = forwardRef<TestimonialFormRef, TestimonialFormProps>(
	function TestimonialForm(
		{ mode, initialData, testimonialId, avatarFile, onSubmit, onLoadingChange, onFormChange },
		ref
	) {
		const { form, errors, isLoading, set, handleSubmit, isDirty } = useTestimonialForm({
			initialData,
			testimonialId,
			avatarFile,
			onSubmit,
		});

		useImperativeHandle(ref, () => ({ submit: handleSubmit, isDirty }));

		useEffect(() => {
			onLoadingChange(isLoading);
		}, [isLoading, onLoadingChange]);

		useEffect(() => {
			onFormChange?.(form);
		}, [form, onFormChange]);

		const isView = mode === 'view';

		if (isView) {
			return (
				<div className={styles.wrapper}>
					<FormSectionTitle>IDENTIFICAÇÃO DO DEPOIMENTO</FormSectionTitle>
					<div className={styles.viewGrid}>
						<div className={styles.viewField}>
							<span className={styles.viewLabel}>Nome</span>
							<span className={styles.viewValue}>{form.name || '—'}</span>
						</div>
						<div className={styles.viewField}>
							<span className={styles.viewLabel}>Cargo Ocupado</span>
							<span className={styles.viewValue}>{form.occupation || '—'}</span>
						</div>
						<div className={styles.viewField}>
							<span className={styles.viewLabel}>Mostrar na Landing Page</span>
							<span className={styles.viewValue}>{STATUS_LABELS[form.status] || '—'}</span>
						</div>
						<div className={`${styles.viewField} ${styles.fullWidth}`}>
							<span className={styles.viewLabel}>Depoimento</span>
							<span className={styles.viewValue}>{form.testimonial || '—'}</span>
						</div>
					</div>
				</div>
			);
		}

		return (
			<div className={styles.wrapper}>
				<FormSectionTitle>IDENTIFICAÇÃO DO DEPOIMENTO</FormSectionTitle>
				<div className={styles.formGrid}>
					<div className={styles.fullWidth}>
						<FormField label="Nome" required error={errors.name}>
							<FormInput
								value={form.name}
								onChange={e => set('name', e.target.value)}
								placeholder="Ex.: Júlia"
								hasError={!!errors.name}
							/>
						</FormField>
					</div>
					<FormField label="Cargo Ocupado" required error={errors.occupation}>
						<FormInput
							value={form.occupation}
							onChange={e => set('occupation', e.target.value)}
							placeholder="Ex.: Consultor"
							hasError={!!errors.occupation}
						/>
					</FormField>
					<FormField label="Mostrar na Landing Page" required error={errors.status}>
						<FormSelect
							value={form.status}
							onChange={v => set('status', v)}
							options={STATUS_OPTIONS}
							placeholder="Selecione a opção"
							hasError={!!errors.status}
						/>
					</FormField>
					<div className={styles.fullWidth}>
						<FormField label="Depoimento">
							<textarea
								className={styles.textarea}
								value={form.testimonial}
								onChange={e => set('testimonial', e.target.value)}
								placeholder="Ex.: O Practium é simplesmente incrível..."
								rows={6}
							/>
						</FormField>
					</div>
				</div>
			</div>
		);
	}
);
